import { DatePipe } from "@angular/common";
import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputValidationIconComponent } from "@components/input-validation-icon/input-validation-icon.component";
import { InputComponent } from "@components/input/input.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateDirective, TranslatePipe, TranslateService } from "@ngx-translate/core";
import _ from "lodash";
import { BehaviorSubject, Observable, Subject, interval } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { ToastService } from "~app/common/components/toast/toast.service";
import { AnvandareDTO } from "~app/common/models/anvandare/AnvandareDTO";
import { Anskaffningsformer, CertifikatTyper, Kontakter, RaskRoller } from "~app/common/models/constants";
import { KontaktDTO } from "~app/common/models/kontakter/KontaktDTO";
import { MomsnummerValideringsstatus } from "~app/common/models/momsnummerValideringsstatus";
import { UpdatedVirkesLeverantorMomsValuesDTO } from "~app/common/models/virkeskop/v2/UpdatedVirkesLeverantorMomsValuesDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { ModelActionService } from "~app/common/services/cqs/model-action.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { StringUtils } from "~app/common/utils/string-utils";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ValidationContext } from "~app/common/validation/validationContext";
import { IMarknadsprislistaSearchParamsModel } from "~app/features/virkeskop/models/marknadsprislista-model";
import { AvtalService } from "~app/features/virkeskop/services/avtal.service";
import { MarknadsprislistaService } from "~app/features/virkeskop/services/marknadsprislista.service";
import { DisabledControlDirective } from "../../../../../common/directives/disabled-control/disabled-control.directive";
import { ValidationMessageContainerComponent } from "../../../../../common/validation/validation-message-container/validation-message-container.component";
import { ValidationMessageComponent } from "../../../../../common/validation/validation-message/validation-message.component";
import { ValidationRuleDirective } from "../../../../../common/validation/validation-rule.directive";
import { ILeverantor } from "../../models/virkesleverantorModel";
import { BetalningsinformationMediatorService } from "../betalningsinformation-mediator.service";
import { LeverantorService } from "../leverantor.service";
import { LeverantorValidation } from "../leverantor.validation";
import { RedigeraAnsvarigVirkeskopareComponent } from "../redigera-leverantor/redigera-ansvarig-virkeskopare/redigera-ansvarig-virkeskopare.component";
import { RedigeraIntressentComponent } from "../redigera-leverantor/redigera-intressent/redigera-intressent.component";
import { BetalningsinformationChangeEvent, BetalningsinformationComponent } from "./betalningsinformation/betalningsinformation.component";
import { ExternOrganisationChangeEvent, ExternaOrganisationerComponent } from "./externa-organisationer/externa-organisationer.component";

@Component({
    selector: "rask-redigera-leverantor-dialog",
    styleUrls: ["./redigera-leverantor-dialog.component.scss"],
    templateUrl: "./redigera-leverantor-dialog.component.html",
    providers: [BetalningsinformationMediatorService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateDirective,
        FormsModule,
        ReactiveFormsModule,
        TabsComponent,
        TabComponent,
        ValidationMessageComponent,
        DisabledControlDirective,
        InputComponent,
        ValidationRuleDirective,
        InputValidationIconComponent,
        TextmultilineComponent,
        CheckboxComponent,
        ValidationMessageContainerComponent,
        BetalningsinformationComponent,
        SelectorComponent,
        RedigeraAnsvarigVirkeskopareComponent,
        RedigeraIntressentComponent,
        SimpleSelectorComponent,
        ExternaOrganisationerComponent,
        MessagePanelComponent,
        SaveButtonComponent,
        ButtonComponent,
        DatePipe,
        TranslatePipe
    ]
})
export class RedigeraLeverantorDialogComponent implements OnInit, OnDestroy {
    readonly activeModal = inject(NgbActiveModal);

    anskaffningsformer = [];
    betalningsmetoder = [];
    certifikatFSC = [];
    certifikatPEFC = [];
    externaOrganisationer = ["Kopparfors"];
    formLeverantor: UntypedFormGroup;
    grupperingar = [];
    guid: string;
    klassningar = [];
    kontakt: KontaktDTO = {} as KontaktDTO;
    kontaktid: number;
    momsnummerValideringsstatus = MomsnummerValideringsstatus;
    momssatser = [];
    newleverantor: ILeverantor;
    position3IntnrOptions = [];
    prislistor = [];
    utbos = [];
    validationContext: ValidationContext;
    protected readonly aggregatedErrorsAndWarnings = new BehaviorSubject<IMessageModel[]>([]);

    private readonly authenticationService = inject(AuthenticationService);
    private readonly avtalService = inject(AvtalService);
    private readonly betalningsinformationMediatorService = inject(BetalningsinformationMediatorService);
    private readonly busyService = inject(BusyService);
    private readonly dirtyFormService = inject(DirtyFormService);
    private readonly editRoles: string = `${RaskRoller.role.Kopleda},${RaskRoller.role.Virkeskopa}`;
    private readonly formBinder = inject(FormBinderService);
    private readonly leverantorService = inject(LeverantorService);
    private readonly marknadsprislistaService = inject(MarknadsprislistaService);
    private readonly modelActionService = inject(ModelActionService);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);
    private readonly unsubscribe$ = new Subject<void>();

    private externOrganisationerValid = true;
    private initialOrgNummer: string;

    betalningValueFormatter = (metod) => metod ? metod.id : "";

    betalningViewFormatter = (metod) => metod ? metod.namn : "";

    canEditTypAvKontakt() {
        return this.isAnbudsleverantor() || (this.kontakt.typAvKontakt == null || this.kontakt.typAvKontakt.length === 0);
    }

    canHaveHuvudKodInternnummerViol3() {
        return this.isOrgnummerValid();
    }

    close(dismiss: boolean) {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if (dismiss) {
            this.activeModal.dismiss("cancel");
        } else {
            this.activeModal.close(this.kontakt);
        }
    }

    createLeverantor(newleverantor: ILeverantor) {
        this.leverantorService.getCreateLeverantor(newleverantor?.guid).subscribe(result => {
            this.initLeverantor(result, newleverantor);
        });
    }

    createLime() {
        this.validationContext.scope(ValidationScopes.skapa, ValidationErrorLevels.error).subscribe(scope => {

            if (this.validationContext.validate(true)) {
                if (!this.isDirty()) {
                    const createObs = this.leverantorService.createLime(this.kontakt.id);
                    this.busyService.addWork(createObs).subscribe(result => {
                        this.showCreated(result.changes.limeId);
                    });
                }
            }
        });
    }

    dirtyCheckCallback(result: IDirtyFormResult) {
        if (result.isSkipSave) {
            this.formLeverantor.markAsPristine();
            // close and dismiss
            this.close(true);
        }
    }

    formatter = (result: string) => result;

    getHeader(): string {
        if (this.isNew()) {
            return "Skapa leverantör";
        }
        const header = this.isAnbudsleverantor() ?
            this.translateService.instant("LEVERANTOR_HEADER_ANBUDSLEVERANTOR") :
            this.translateService.instant("LEVERANTOR_HEADER_LEVERANTOR");
        const name = this.kontakt.kontaktNamn ? this.kontakt.kontaktNamn : "";
        return `${header} ${name}`;
    }

    getMomskodText(momskodid: string) {
        const text = this.momssatser.filter(x => x.id === momskodid);
        return text && text.length > 0 ? text[0].namn : momskodid;
    }

    getMomsnummerStatusIcon(): string {
        if (this.kontakt.momsnummerValideringsstatus === MomsnummerValideringsstatus.ogiltig) {
            if (this.kontakt.momsnummerEjAktuellt) {
                return "";
            }
            return "far fa-exclamation-circle";
        } else if (this.kontakt.momsregistreringsnummer !== this.kontakt.momsnummerKontrollerat) {
            return "far fa-exclamation-square";
        } else if (this.kontakt.momsnummerValideringsstatus === MomsnummerValideringsstatus.ejValiderad) {
            return "far fa-exclamation-square";
        }
        return "";
    }

    getMomsnummerStatusIconColor(): string {
        if (this.kontakt.momsnummerValideringsstatus === MomsnummerValideringsstatus.ogiltig) {
            return "error";
        } else if (this.kontakt.momsregistreringsnummer !== this.kontakt.momsnummerKontrollerat) {
            return "warn";
        } else if (this.kontakt.momsnummerValideringsstatus === MomsnummerValideringsstatus.ejValiderad) {
            return "warn";
        }
        return "";
    }

    getViesStatusText(): string {
        switch (this.kontakt.momsnummerValideringsstatus) {
            case MomsnummerValideringsstatus.ejValiderad:
                return "Kontroll kunde ej genomföras";
            case MomsnummerValideringsstatus.ogiltig:
                return "Ogiltigt";
            default:
                return "";
        }
    }

    handleBetalningsinformationChange(event: BetalningsinformationChangeEvent) {
        this.formLeverantor.patchValue({
            ...event.betalningsinformation,
            createBetalningsinformationUtkast: true
        });
        this.formLeverantor.markAsDirty();
    }

    handleExternOrganisationerChange(event: ExternOrganisationChangeEvent) {
        this.kontakt.externOrganisationer = event.externOrganisationer;
        this.externOrganisationerValid = event.isValid;
        this.formLeverantor.markAsDirty();
    }

    handleMomsInfo(momsInfo: UpdatedVirkesLeverantorMomsValuesDTO) {
        this.formLeverantor.get("momskod").setValue(momsInfo.momskod);
        this.formLeverantor.get("momsnummerValideringsstatus").setValue(momsInfo.momsnummerValideringsstatus);
        this.formLeverantor.get("momsnummerValideringstid").setValue(momsInfo.momsnummerValideringstid);
        this.formLeverantor.get("momsnummerKontrollerat").setValue(momsInfo.momsnummerKontrollerat);

        this.triggerValidation();
    }

    hasEditRole(): boolean {
        return this.authenticationService.hasRoles(this.editRoles);
    }

    hasKopleda(): boolean {
        return this.authenticationService.hasRole(RaskRoller.role.Kopleda);
    }

    initLeverantor(kontakt: KontaktDTO, newleverantor: ILeverantor) {
        this.kontakt = kontakt;
        this.initialOrgNummer = kontakt.kontaktOrgnummer;

        this.mapFromLeverantor(this.kontakt, newleverantor);
        this.formLeverantor = this.formBinder.CreateGroup(this.kontakt);

        this.modelActionService.reset();
        this.modelActionService.bindModel(this.kontakt, "Kontakt");
        this.validationContext = LeverantorValidation.init(this.translateService, this.kontakt);
        // Nödlösning för att inte spamma message-panel med massor av meddelanden som är identiska
        interval(300).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            this.generateValidationMessages();
        });

        this.dirtyFormService.onDirty(
            this.formLeverantor,
            () => this.save(this.isLeverantor()),
            d => this.dirtyCheckCallback(d)
        );

        if (!this.hasEditRole()) {
            this.formLeverantor.disable();
        }
    }

    integrationAnsvarigVirkeskoparechanged(a: AnvandareDTO) {
        setTimeout(() => {
            this.kontakt.integrationAnsvarigVirkeskopare = a;
            this.formLeverantor.markAsDirty();
        });
    }

    integrationExternOrganisationSelected(): boolean {
        return ObjectUtils.hasValue(this.formLeverantor.get("integrationExternOrganisation")?.value);
    }

    intressentadded(anvandare: AnvandareDTO) {
        this.modelActionService.addNewChildModel(anvandare, "Intressent", this.kontakt, "id");
        this.formLeverantor.markAsDirty();
    }

    intressentremoved(anvandare: AnvandareDTO) {
        this.modelActionService.removeModel(anvandare, "Intressent", this.kontakt);
        this.formLeverantor.markAsDirty();
    }

    isAnbudsleverantor() {
        return !this.isLeverantor();
    }

    isBetalningsmetod(metod: string): boolean {
        return metod.toLowerCase() === this.kontakt.betalningsmetod.toLowerCase();
    }

    isDirty() {
        const hasQueue = this.modelActionService.commandQueue && this.modelActionService.commandQueue.length > 0;
        return hasQueue && this.formLeverantor.dirty;
    }

    isLeverantor() {
        return (this.kontakt?.kontaktLimeId?.length > 0);
    }

    isLoaded = () => this.kontakt != null && this.formLeverantor != null && this.validationContext != null;

    isMomsValid(): boolean {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (this.kontakt.momsnummerValideringsstatus === MomsnummerValideringsstatus.giltig && this.kontakt.momsnummerKontrollerat === this.kontakt.momsregistreringsnummer) {
            return true;
        }
        return false;
    }

    isMomsnummerOgiltig(): boolean {
        return this.kontakt.momsnummerValideringsstatus === MomsnummerValideringsstatus.ogiltig;
    }

    isMomsnummerValid(): boolean {
        // eslint-disable-next-line sonarjs/concise-regex
        const regexp = new RegExp("^SE[0-9]{12}$");
        return regexp.test(this.kontakt.momsregistreringsnummer);
    }

    isNew() {
        return this.kontakt.id == null || this.kontakt.id === 0;
    }

    isOrgnummerValid(): boolean {
        const regexp = new RegExp(Kontakter.PersOrgnummerRegularExpression);
        return regexp.test(this.kontakt.kontaktOrgnummer);
    }

    isPristine() {
        return this.formLeverantor.pristine;
    }

    loadLeverantor(id: number) {
        this.leverantorService.getLeverantor(id).subscribe(result => {
            this.initLeverantor(result, null);
            this.triggerValidation();
        });
    }

    mapFromLeverantor(kontakt: KontaktDTO, leverantor: ILeverantor) {
        if (!leverantor || !!kontakt?.id) {
            return;
        }
        kontakt.guid = kontakt.guid ?? leverantor.guid;
        kontakt.kontaktNamn = leverantor.namn;
        kontakt.kontaktpersonFornamn = leverantor.kontaktFornamn;
        kontakt.kontaktpersonEfternamn = leverantor.kontaktEfternamn;
        kontakt.kontaktAdress = leverantor.adress;
        kontakt.kontaktAdress2 = leverantor.adress2;
        kontakt.kontaktPostnummer = leverantor.postnummer;
        kontakt.kontaktPostort = leverantor.postort;
        kontakt.kontaktOrgnummer = leverantor.persOrgNummer;
        kontakt.typAvKontakt = leverantor.kontaktTypAvKontakt;
        kontakt.position3Intnr = leverantor.position3Intnr;
        kontakt.momskod = leverantor.momskod;
        kontakt.momsregistreringsnummer = leverantor.momsregistreringsnummer;
        kontakt.momsnummerKontrollerat = leverantor.momsnummerKontrollerat;
        kontakt.momsnummerValideringsstatus = leverantor.momsnummerValideringsstatus;
        kontakt.momsnummerValideringstid = leverantor.momsnummerValideringstid;
        kontakt.anskaffningsform = leverantor.anskaffningsform;
    }

    momsnummerChanged() {
        this.triggerValidation();
    }

    momsnummerEjAkutelltChanged(checked: boolean) {
        if (checked) {
            const user = this.authenticationService.getUserData();
            this.formLeverantor.get("momsnummerEjAktuelltAnsvarig").setValue({
                fornamn: user.fullUserName
            });
            this.formLeverantor.get("momsnummerEjAktuelltTidpunkt").setValue(new Date().toISOString());
            this.formLeverantor.get("momsregistreringsnummer").setValue("");
        } else {
            this.formLeverantor.get("momsnummerEjAktuelltTidpunkt").setValue(null);
            this.formLeverantor.get("momsnummerEjAktuelltAnsvarig").setValue(null);
        }
        this.triggerValidation();
    }

    ngOnDestroy(): void {
        this.aggregatedErrorsAndWarnings.complete();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    async ngOnInit() {
        this.utbos = this.leverantorService.getUtboAbo();
        this.betalningsmetoder = this.leverantorService.getBetalningsmetoder();
        this.klassningar = this.leverantorService.getKlassningar();
        this.grupperingar = this.leverantorService.getGrupperingar();
        this.momssatser = this.leverantorService.getMomssatser();
        this.position3IntnrOptions = this.leverantorService.getPosition3IntnrOptionsOptions();
        this.avtalService.getCertifikat().pipe(map(c => {
            this.certifikatPEFC = c.filter(x => x.certifikatstyp === CertifikatTyper.Pefc && x.aktiv === true);
            this.certifikatFSC = c.filter(x => x.certifikatstyp === CertifikatTyper.Fsc && x.aktiv === true);
        })).subscribe();
        this.marknadsprislistaService.getMarknadsprislistor({ isAktuell: true } as IMarknadsprislistaSearchParamsModel)
            .subscribe(prislistor => {
                this.prislistor = prislistor;
            });

        if (this.kontaktid != null && this.kontaktid > 0) {
            this.loadLeverantor(this.kontaktid);
        } else {
            this.createLeverantor(this.newleverantor);
        }

        this.anskaffningsformer = [
            Anskaffningsformer.Virkeskop,
            Anskaffningsformer.CentralaVirkeskop
        ];
    }

    onChangeOrgnummer() {
        const currentNr = this.formLeverantor.get("kontaktOrgnummer").value;

        if (currentNr && (currentNr.length === 10 || currentNr.length === 12) && currentNr.indexOf("-") === -1) {
            const indexPos = currentNr.length === 10 ? 6 : 8;
            this.formLeverantor.get("kontaktOrgnummer").setValue(StringUtils.insertAt(currentNr, indexPos, "-"));
        }

        if (this.isOrgnummerValid()) {
            this.recalculateMomsregistreringskod();
            this.validateVies();
        }
    }

    onNamnChange() {
        if (this.kontakt.typAvKontakt === "Privatperson") {
            this.kontakt.kontaktNamn = `${this.kontakt.kontaktpersonFornamn} ${this.kontakt.kontaktpersonEfternamn}`;
            this.triggerValidation();
        }
    }

    onTypAvKontaktChange($event) {
        this.triggerValidation();
    }

    position3IntnrValueFormatter = (metod) => metod ? metod.id : null;

    position3IntnrViewFormatter = (metod) => metod ? metod.namn : "";

    recalculateMomsregistreringskod() {
        const momsreg = this.generateMomsregistreringskod(this.kontakt?.kontaktOrgnummer ?? "");
        this.formLeverantor.get("momsregistreringsnummer").setValue(momsreg);
        this.formLeverantor.markAsDirty();
    }

    reloadLeverantor(id: number): Observable<KontaktDTO> {
        return this.leverantorService.getLeverantor(id);
    }

    save(close: boolean) {
        const { createBetalningsinformationUtkast } = this.formLeverantor.value;

        this.validationContext.scope(ValidationScopes.spara, ValidationErrorLevels.error).subscribe(scope => {
            if (this.validationContext.validate(true) && this.externOrganisationerValid) {
                const saveObs = this.modelActionService.pushCommandQueue();
                this.busyService.addWork(saveObs).subscribe(result => {
                    const entityId = result?.length > 0 ? result[0].entityId : null;
                    this.kontakt.id = entityId;
                    this.reloadLeverantor(entityId).subscribe(kontakt => {
                        this.kontakt = kontakt;
                        this.initialOrgNummer = kontakt.kontaktOrgnummer;
                        this.formLeverantor.markAsPristine();
                        this.betalningsinformationMediatorService.save();
                        if (this.isLeverantor()) {
                            let message = this.translateService.instant("LEVERANTOR_LEVERANTOREN_SPARAD");
                            if (createBetalningsinformationUtkast) {
                                message += " och utkast för betalningsinformation har skapats";
                            }
                            this.toastService.add(message);
                        } else {
                            this.toastService.add(this.translateService.instant("LEVERANTOR_ANBUDSLEVERANTOREN_SPARAD"));
                        }
                        if (close && !createBetalningsinformationUtkast) {
                            this.tryClose();
                        }
                    });
                });
            }
        });
    }

    saveNewAndCreateLime() {
        this.validationContext.scope(ValidationScopes.skapa, ValidationErrorLevels.error).subscribe(scope => {
            if (this.validationContext.validate(true)) {
                if (this.isDirty()) {
                    const saveObs = this.modelActionService.pushCommandQueue();
                    this.busyService.addWork(saveObs).subscribe(r => {
                        this.formLeverantor.markAsPristine();
                        const createObs = this.leverantorService.createLime(this.kontakt.id);
                        this.busyService.addWork(createObs).subscribe(result => {
                            this.showCreated(result.changes.limeId);
                        });
                    });
                } else {
                    const createObs = this.leverantorService.createLime(this.kontakt.id);
                    this.busyService.addWork(createObs).subscribe(result => {
                        this.showCreated();
                    });
                }
            }
        });
    }

    searchForHuvudKod = (searchString: string) => this.leverantorService
        .getViol3AktorData(this.kontakt.kontaktOrgnummer)
        .pipe(
            map(result => result.map(item => `${item.huvudkod}-${item.internnummer}`))
        );

    showCreated(limeid = null) {
        if (limeid != null) {
            this.formLeverantor.get("kontaktLimeId").setValue(limeid);
        }
        this.toastService.add(this.translateService.instant("LEVERANTOR_KONTAKT_SKAPAD"));
        this.tryClose();
    }

    tryClose(dismiss = false) {
        if (this.dirtyFormService.canClose()) {
            this.close(dismiss);
        }
    }

    validateVies(): void {
        if (this.kontakt.momsnummerEjAktuellt) {
            return;
        }
        if (!this.isMomsnummerValid()) {
            this.triggerValidation();
            return;
        }
        const id = this.kontakt?.id ?? 0;
        this.busyService
            .addWork(this.leverantorService.updateAndGetChangesForViesValidation(id, this.kontakt.momsregistreringsnummer))
            .subscribe(momsInfo => {
                this.handleMomsInfo(momsInfo);
            });
    }

    virkeskoparechanged(a: AnvandareDTO) {
        setTimeout(() => {
            this.kontakt.ansvarigVirkeskopare = a;
            this.formLeverantor.markAsDirty();
            // eslint-disable-next-line sonarjs/deprecation
            event.preventDefault();
        });
    }

    protected hasValidationErrors(): boolean {
        return this.validationContext?.current?.rules?.some(x => x.hasError || x.hasWarning);
    }

    protected orgnummerDisabled(): boolean {

        // Om det är en leverantör (har LimeId) och ett personnummer/orgnummer angivet och leverantören är sparad ska det inte gå att ändra.
        // Anbudsleverantörer ska kunna ändra på orgnummer/personnummer
        if (ObjectUtils.isNullOrUndefined(this.kontakt.kontaktLimeId)) {
            return false;
        }

        if (this.shouldDisableOrgnummer()) {
            return true;
        }

        return false;
    }

    protected shouldDisableOrgnummer(): boolean {
        return !_.isNil(this.kontakt.kontaktOrgnummer) &&
            this.kontakt.id &&
            this.isOrgnummerValid() &&
            !_.isNil(this.initialOrgNummer);
    }

    protected triggerValidation(): void {
        this.validationContext.validate();
    }

    private generateMomsregistreringskod(kontaktOrgnummer: string): string {
        const nonCenturyOrgnummer = kontaktOrgnummer.length === 13 ? kontaktOrgnummer.substring(2) : kontaktOrgnummer;
        return `SE${nonCenturyOrgnummer.replace(/-/g, "").replace(/ /g, "")}01`;
    }

    private generateValidationMessages() {
        const errorsAndWarnings = this.validationContext?.current?.rules?.filter(x => x.hasError || x.hasWarning);
        const errorMessages = errorsAndWarnings.map(x => (({
            severity: x.isWarning ? MessageSeverity.Warning : MessageSeverity.Error,
            message: x.message.text
        }) as IMessageModel));
        this.aggregatedErrorsAndWarnings.next(errorMessages);
    }
}
